import React from "react";
import {
  Box, TextField, Button, Checkbox, FormControlLabel, Paper, List, ListItem, ClickAwayListener, Grid, Typography
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { MuiTelInput } from "mui-tel-input";
import { useTranslation } from "react-i18next";
import NovaPoshtaCitySelect from "./NovaPoshtaCitySelect";
import NovaPoshtaWarehouseSelect from "./NovaPoshtaWarehouseSelect";

const getFieldError = (errors, key) => {
  if (!errors || typeof errors !== "object") return undefined;
  const msg = errors[key];
  return Array.isArray(msg) ? msg[0] : msg;
};

const NewPackageForm = ({
  newPackage, setNewPackage,
  customerOptions, customerSearchInput, setCustomerSearchInput,
  showPhoneDropdown, phoneSuggestions, handlePhoneSuggestionSelect,
  originalCustomer, setOriginalCustomer,
  originalAddress, setOriginalAddress, setShowPhoneDropdown,
  handleAdd,
  onEditCustomer,
  onClearCustomer,
  errors = {},
  onClearFieldError,
}) => {
  const { t } = useTranslation("common");
  const capitalizeName = (val) => {
    if (!val) return "";
    return String(val)
      .toLowerCase()
      .replace(/(^|[\s-])([\p{L}])/gu, (m, sep, ch) => `${sep}${ch.toUpperCase()}`);
  };
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("form.requiredNote")}
      </Typography>
      <Grid container spacing={2}>
        {/* Адреса доставки */}
        <Grid item xs={12}>
          {getFieldError(errors, "address.non_field_errors") && (
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
              {getFieldError(errors, "address.non_field_errors")}
            </Typography>
          )}
          <Typography variant="subtitle1" fontWeight={600}>{t("form.deliveryAddress")}</Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={newPackage.address.is_nova_poshta}
                onChange={(e) =>
                  setNewPackage((prev) => ({
                    ...prev,
                    address: {
                      ...prev.address,
                      is_nova_poshta: e.target.checked,
                      city: "",
                      warehouse: ""
                    }
                  }))
                }
              />
            }
            label={t("form.novaPoshta")}
          />
        </Grid>

        {newPackage.address.is_nova_poshta ? (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <Typography component="label" variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {t("form.city")} *
                </Typography>
                <NovaPoshtaCitySelect
                  value={newPackage.address.city}
                  onChange={(val) => {
                    onClearFieldError?.("address.city");
                    setNewPackage((prev) => ({
                      ...prev,
                      address: { ...prev.address, city: val, warehouse: "" }
                    }));
                  }}
                />
                {getFieldError(errors, "address.city") && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    {getFieldError(errors, "address.city")}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <Typography component="label" variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {t("packagesAdmin.warehouse")}
                </Typography>
                <NovaPoshtaWarehouseSelect
                  cityName={newPackage.address.city}
                  value={newPackage.address.warehouse}
                  onChange={(val) => {
                    onClearFieldError?.("address.warehouse");
                    setNewPackage((prev) => ({
                      ...prev,
                      address: { ...prev.address, warehouse: val }
                    }));
                  }}
                />
                {getFieldError(errors, "address.warehouse") && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    {getFieldError(errors, "address.warehouse")}
                  </Typography>
                )}
              </Box>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                size="small"
                label={t("form.city")}
                fullWidth
                required
                value={newPackage.address.city}
                error={!!getFieldError(errors, "address.city")}
                helperText={getFieldError(errors, "address.city")}
                onChange={(e) => {
                  onClearFieldError?.("address.city");
                  setNewPackage((prev) => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value }
                  }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                size="small"
                label={t("form.address")}
                fullWidth
                required
                value={newPackage.address.street}
                error={!!getFieldError(errors, "address.street")}
                helperText={getFieldError(errors, "address.street")}
                onChange={(e) => {
                  onClearFieldError?.("address.street");
                  setNewPackage((prev) => ({
                    ...prev,
                    address: { ...prev.address, street: e.target.value }
                  }));
                }}
              />
            </Grid>
          </>
        )}

        {/* Дані отримувача */}
        <Grid item xs={12} mt={2}>
          {getFieldError(errors, "customer.non_field_errors") && (
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
              {getFieldError(errors, "customer.non_field_errors")}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={600}>{t("form.recipient")}</Typography>
            {newPackage.customer.customer_id && (onEditCustomer || onClearCustomer) && (
              <Box sx={{ display: "flex", gap: 1 }}>
                {onEditCustomer && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onEditCustomer}
                  >
                    {t("form.edit")}
                  </Button>
                )}
                {onClearCustomer && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={onClearCustomer}
                  >
                    {t("form.clear")}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ position: "relative" }}>
            <MuiTelInput
              defaultCountry="UA"
              fullWidth
              size="small"
              label={t("admin.phone")}
              required
              value={newPackage.customer.phone_number}
              disabled={!!newPackage.customer.customer_id}
              error={!!getFieldError(errors, "customer.phone_number")}
              helperText={getFieldError(errors, "customer.phone_number")}
              onChange={(value) => {
                onClearFieldError?.("customer.phone_number");
                setNewPackage((prev) => ({
                  ...prev,
                  customer: { ...prev.customer, phone_number: value },
                }));
              }}
            />
            {showPhoneDropdown && phoneSuggestions.length > 0 && !newPackage.customer.customer_id && (
              <ClickAwayListener onClickAway={() => setShowPhoneDropdown(false)}>
                <Paper sx={{ position: "absolute", zIndex: 10, width: "100%", maxHeight: 200, overflowY: "auto" }}>
                  <List dense>
                    {phoneSuggestions.map((sug) => (
                      <ListItem key={sug.customer_id} button onClick={() => handlePhoneSuggestionSelect(sug)}>
                        {capitalizeName(sug.first_name)} {capitalizeName(sug.last_name)} — {sug.phone_number}
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </ClickAwayListener>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            fullWidth
            label={t("form.name")}
            required
            value={newPackage.customer.first_name}
            disabled={!!newPackage.customer.customer_id}
            error={!!getFieldError(errors, "customer.first_name")}
            helperText={getFieldError(errors, "customer.first_name")}
            onChange={(e) => {
              onClearFieldError?.("customer.first_name");
              setNewPackage((prev) => ({
                ...prev,
                customer: { ...prev.customer, first_name: capitalizeName(e.target.value) },
              }));
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            freeSolo
            disabled={!!newPackage.customer.customer_id}
            options={customerOptions || []}
            getOptionLabel={(option) =>
              typeof option === "object"
                ? `${capitalizeName(option.first_name)} ${capitalizeName(option.last_name)} (${option.phone_number})`
                : option
            }
            inputValue={newPackage.customer.last_name}
            onInputChange={(_, val) => setCustomerSearchInput(val)}
            onChange={(_, selected) => {
              if (selected) {
                const customer = {
                  first_name: capitalizeName(selected.first_name),
                  last_name: capitalizeName(selected.last_name),
                  phone_number: selected.phone_number,
                  customer_id: selected.customer_id,
                };
                const address = {
                  ...selected.last_address,
                  address_id: selected.last_address?.address_id || null,
                };
                setOriginalCustomer(customer);
                setOriginalAddress(address);
                setNewPackage((prev) => ({
                  ...prev,
                  customer,
                  address,
                }));
                setCustomerSearchInput(capitalizeName(selected.last_name));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                label={t("form.surname")}
                required
                error={!!getFieldError(errors, "customer.last_name")}
                helperText={getFieldError(errors, "customer.last_name")}
                value={newPackage.customer.last_name}
                onChange={(e) => {
                  onClearFieldError?.("customer.last_name");
                  if (!newPackage.customer.customer_id) {
                    setNewPackage((prev) => ({
                      ...prev,
                      customer: { ...prev.customer, last_name: capitalizeName(e.target.value) },
                    }));
                    setCustomerSearchInput(e.target.value);
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Параметри посилки */}
        <Grid item xs={12} mt={2}>
          <Typography variant="subtitle1" fontWeight={600}>{t("form.packageParams")}</Typography>
        </Grid>

        <Grid item xs={12} sm={3} md={2}>
          <TextField
            type="number"
            size="small"
            fullWidth
            label={t("form.quantity")}
            required
            value={newPackage.quantity ?? ""}
            inputProps={{ min: 1 }}
            error={!!getFieldError(errors, "quantity")}
            helperText={getFieldError(errors, "quantity")}
            onChange={(e) => {
              onClearFieldError?.("quantity");
              setNewPackage({ ...newPackage, quantity: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <TextField
            type="number"
            size="small"
            fullWidth
            label={t("form.weightKg")}
            required
            value={newPackage.weight_kg ?? ""}
            inputProps={{ min: 0, step: 0.01 }}
            error={!!getFieldError(errors, "weight_kg")}
            helperText={getFieldError(errors, "weight_kg")}
            onChange={(e) => {
              onClearFieldError?.("weight_kg");
              setNewPackage({ ...newPackage, weight_kg: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <TextField
            type="number"
            size="small"
            fullWidth
            label={t("form.priceEur")}
            required
            value={newPackage.price ?? ""}
            inputProps={{ min: 0, step: 0.01 }}
            error={!!getFieldError(errors, "price")}
            helperText={getFieldError(errors, "price")}
            onChange={(e) => {
              onClearFieldError?.("price");
              setNewPackage({ ...newPackage, price: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <TextField
            type="text"
            size="small"
            fullWidth
            label={t("form.internalNumber")}
            value={newPackage.internal_number ?? ""}
            error={!!getFieldError(errors, "internal_number")}
            helperText={getFieldError(errors, "internal_number")}
            onChange={(e) => {
              onClearFieldError?.("internal_number");
              setNewPackage({ ...newPackage, internal_number: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            type="text"
            size="small"
            fullWidth
            label={t("form.comment")}
            value={newPackage.comment}
            onChange={(e) => setNewPackage({ ...newPackage, comment: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} sm={3} md={2}>
          <Button variant="contained" fullWidth sx={{ height: "100%" }} onClick={handleAdd}>
            {t("admin.add")}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default NewPackageForm;
